/**
 * useProposalReviewFlow — the bulk-resolve review queue, end to end.
 *
 * A resolve pass no longer applies its rewrites; it records each changed
 * paragraph as a pending proposal (see the server ``section_proposals`` store).
 * This hook loads that queue and drives the writer through it one proposal at a
 * time — Approve / Reject / Revise — revealing each change in the open Word
 * document as it becomes the active card.
 *
 *   idle → loading → reviewing → (applying | revising) → reviewing → … → idle
 *
 * Approve: the server applies the paragraph to the section SSOT + reconciles the
 * ledger (``decideProposal``), then this hook mirrors the ONE approved paragraph
 * into the open Word document (same section-scoped replace the chat highlight
 * edit uses). Reject: the server marks it rejected and leaves the issue open.
 * Revise: a fresh rewrite that keeps both the original and the current proposed
 * text in context, producing a new proposal that supersedes this one.
 *
 * A monotonic ``flowTokenRef`` guards every async settle so a stale stream or a
 * superseded decision can't resurrect a proposal the writer already moved past.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  decideProposal,
  getRunProposals,
  type ResolveIssueDoc,
  reviseProposalStream,
  type SectionProposal,
} from "../../api/client";
import { needsFormatterRender } from "../../services/formatter";
import {
  applyTableRowEditsInSection,
  replaceTextInSection,
  replaceTextInSectionStyled,
  type SectionTableRowWrite,
  selectMarkerParagraph,
  selectSpanInSection,
} from "../../services/wordWriter";
import { sanitizeDraftForWord } from "../services/draftSanitizer";
import { diffRow, parsePipeRow } from "../services/tableEdit";
import { labelForSelectionEditEvent } from "./selectionEditLabels";

/** One reviewable proposal plus the section number/title for its header +
 *  the Word write/reveal target. */
export interface ProposalQueueItem {
  proposal: SectionProposal;
  sectionNumber: string;
  sectionTitle: string;
}

export type ProposalReviewPhase = "idle" | "loading" | "reviewing" | "applying" | "revising";

export interface UseProposalReviewFlowParams {
  runId: string | null;
  /** External re-fetch trigger — bumped by the shell when a resolve pass emits a
   *  proposal / finishes (``proposalsRefreshSignal``). */
  refreshSignal?: number;
  /** Active source library — scopes a Revise rewrite's retrieval. */
  libraryId?: string | null;
  /** Called after a proposal is approved or rejected so the host can refresh the
   *  Issues list (an approve resolved the issue; a reject left it open). */
  onDecided?: () => void;
  /** Surface a writer-readable error. */
  onError?: (message: string) => void;
}

export interface UseProposalReviewFlowResult {
  phase: ProposalReviewPhase;
  /** Every pending proposal, in submission order. */
  queue: ProposalQueueItem[];
  /** Index of the proposal under review (clamped to the queue). */
  activeIndex: number;
  /** The proposal under review, or null when the queue is empty. */
  active: ProposalQueueItem | null;
  /** Live status line for a Revise re-run (null otherwise). */
  progressLabel: string | null;
  error: string | null;
  reload: () => Promise<void>;
  next: () => void;
  prev: () => void;
  select: (index: number) => void;
  approve: () => Promise<void>;
  reject: () => Promise<void>;
  revise: (guidance: string) => void;
}

function flattenQueue(
  sections: Awaited<ReturnType<typeof getRunProposals>>["sections"],
): ProposalQueueItem[] {
  const items: ProposalQueueItem[] = [];
  for (const section of sections) {
    for (const proposal of section.proposals) {
      if (proposal.status === "pending") {
        items.push({ proposal, sectionNumber: section.number, sectionTitle: section.title });
      }
    }
  }
  return items;
}

/** Build the section-scoped Word cell writes for an approved TABLE proposal from
 *  its per-row edits — parse each pipe row back to cells, diff to find the
 *  changed columns, and skip any row the model mangled (column-count change) or
 *  that no longer differs. Returns [] when the proposal isn't a usable table. */
function tableWritesFromProposal(proposal: SectionProposal): SectionTableRowWrite[] {
  if (!proposal.is_table || !proposal.table_row_edits?.length) return [];
  const writes: SectionTableRowWrite[] = [];
  for (const edit of proposal.table_row_edits) {
    const originalCells = parsePipeRow(edit.original_row);
    const proposedCells = parsePipeRow(edit.proposed_row);
    if (!originalCells || !proposedCells) continue;
    const diff = diffRow(originalCells, proposedCells);
    if (!diff) continue; // column count changed — never realign
    const changedColumns = diff.map((c, i) => (c.changed ? i : -1)).filter((i) => i >= 0);
    if (changedColumns.length === 0) continue;
    writes.push({ rowIndex: edit.row_index, cells: proposedCells, changedColumns, originalCells });
  }
  return writes;
}

/** Shown on a refused Approve (409): the paragraph moved or the section
 *  changed after this proposal was prepared, so it can't be applied as is. */
export const APPROVE_CONFLICT_MESSAGE =
  "This paragraph changed after the proposal was made, so it can't be applied as it is. Revise it to update against the current text, or Reject it.";

export function useProposalReviewFlow(params: UseProposalReviewFlowParams): UseProposalReviewFlowResult {
  const { runId, refreshSignal, libraryId, onDecided, onError } = params;
  const [phase, setPhase] = useState<ProposalReviewPhase>("idle");
  const [queue, setQueue] = useState<ProposalQueueItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [progressLabel, setProgressLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Guards async settles against a superseded proposal / stale stream.
  const flowTokenRef = useRef(0);
  const requestKeyRef = useRef("");

  const reload = useCallback(async () => {
    if (!runId) {
      setQueue([]);
      setPhase("idle");
      return;
    }
    const key = `${runId}`;
    requestKeyRef.current = key;
    setPhase("loading");
    setError(null);
    try {
      const resp = await getRunProposals(runId, "pending");
      if (requestKeyRef.current !== key) return;
      const items = flattenQueue(resp.sections);
      setQueue(items);
      setActiveIndex((i) => Math.max(0, Math.min(i, items.length - 1)));
      setPhase(items.length > 0 ? "reviewing" : "idle");
    } catch (err) {
      if (requestKeyRef.current !== key) return;
      setPhase("idle");
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [runId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Re-fetch when the shell signals the queue changed (a pass produced a
  // proposal / finished). Skips the mount value — reload's own effect fetched.
  const refreshSeenRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshSignal is the external re-fetch trigger
  useEffect(() => {
    if (!refreshSeenRef.current) {
      refreshSeenRef.current = true;
      return;
    }
    void reload();
  }, [refreshSignal, reload]);

  const active = queue[activeIndex] ?? null;

  // Reveal in Word: whenever the active proposal changes (mount, next/prev,
  // select), scroll the open document to the paragraph the change would land in
  // — WITHOUT modifying it. Best-effort: a failure to locate is silent.
  const revealedKeyRef = useRef<string>("");
  useEffect(() => {
    if (!active || (phase !== "reviewing" && phase !== "revising")) return;
    const key = `${active.proposal.id}:${active.proposal.revision_count}`;
    if (revealedKeyRef.current === key) return;
    revealedKeyRef.current = key;
    const { proposal, sectionNumber } = active;
    void (async () => {
      try {
        const marker = proposal.marker_texts[0];
        const found = marker ? await selectMarkerParagraph(marker) : false;
        if (!found) await selectSpanInSection(sectionNumber, proposal.original_text);
      } catch {
        // Reveal is a courtesy — never block the review on it.
      }
    })();
  }, [active, phase]);

  const dropActive = useCallback(() => {
    setQueue((prev) => {
      const idx = activeIndex;
      const next = prev.filter((_, i) => i !== idx);
      setActiveIndex((cur) => Math.max(0, Math.min(cur, next.length - 1)));
      setPhase(next.length > 0 ? "reviewing" : "idle");
      return next;
    });
  }, [activeIndex]);

  const next = useCallback(() => {
    setActiveIndex((i) => Math.min(i + 1, Math.max(0, queue.length - 1)));
  }, [queue.length]);
  const prev = useCallback(() => {
    setActiveIndex((i) => Math.max(0, i - 1));
  }, []);
  const select = useCallback(
    (index: number) => {
      if (index >= 0 && index < queue.length) setActiveIndex(index);
    },
    [queue.length],
  );

  const approve = useCallback(async () => {
    if (!runId || !active || phase !== "reviewing") return;
    const token = ++flowTokenRef.current;
    const { proposal, sectionNumber } = active;
    setPhase("applying");
    setError(null);
    try {
      // 1. Server applies to the SSOT + reconciles the ledger (version-checked).
      //    For a table this splices each rewritten row back by index (drift-proof).
      await decideProposal(runId, proposal.id, "approve");
      // 2. Mirror the approved change into the open Word document. The SSOT is
      //    already the source of truth; a Word miss is surfaced but does not roll
      //    back the record (the writer can Load to reconcile).
      const tableWrites = tableWritesFromProposal(proposal);
      let missed = false;
      if (tableWrites.length > 0) {
        // Table proposal: write only the changed cells in place — never a
        // paragraph splice (which would corrupt the table). Row indices are the
        // Word getCell indices the server computed.
        const result = await applyTableRowEditsInSection(sectionNumber, tableWrites);
        missed = result === "not_found";
      } else {
        const cleaned = sanitizeDraftForWord(proposal.proposed_text);
        const styled = needsFormatterRender(cleaned);
        // No prefix hint: first-occurrence replace, matching the server's
        // first-occurrence replace_span_in_draft so Word and the SSOT agree.
        const result = styled
          ? await replaceTextInSectionStyled(sectionNumber, proposal.original_text, cleaned)
          : await replaceTextInSection(sectionNumber, proposal.original_text, cleaned);
        missed = result === "not_found" || result === "table_conflict";
      }
      if (flowTokenRef.current !== token) return;
      if (missed) {
        onError?.(
          `Applied to the record, but couldn't update the open document in §${sectionNumber} — it may have been edited. Load the section to reconcile.`,
        );
      }
      dropActive();
      onDecided?.();
    } catch (err) {
      if (flowTokenRef.current !== token) return;
      setPhase("reviewing");
      // A 409 means the section changed since the pass (span_drift /
      // version_conflict) or the proposal was already decided — refresh the
      // queue. Key off the structured status/detail (client.ts request() sets
      // err.status + err.detail), not the human message string.
      //
      // The reason is shown in the panel's own error line, directly above the
      // card the writer just clicked — the tab-level onError line is out of
      // view, and the reload brings the same (still pending) card back, so a
      // refused Approve used to look like nothing happened. reload() clears
      // the error first, so set it after the reload settles.
      const e = err as { status?: number; detail?: { error?: string }; message?: string };
      const detail = err instanceof Error ? err.message : String(err);
      const isConflict =
        e?.status === 409 || e?.detail?.error === "span_drift" || /span_drift|409/.test(detail);
      if (isConflict) {
        const message =
          e?.detail?.error === "already_decided"
            ? "This proposal was already decided — the list has been refreshed."
            : APPROVE_CONFLICT_MESSAGE;
        void reload().then(() => {
          if (flowTokenRef.current === token) setError(message);
        });
      } else {
        setError(`Couldn't apply the change: ${detail}`);
      }
    }
  }, [runId, active, phase, dropActive, onDecided, onError, reload]);

  const reject = useCallback(async () => {
    if (!runId || !active || phase !== "reviewing") return;
    const token = ++flowTokenRef.current;
    const { proposal } = active;
    setPhase("applying");
    setError(null);
    try {
      await decideProposal(runId, proposal.id, "reject");
      if (flowTokenRef.current !== token) return;
      dropActive();
      onDecided?.();
    } catch (err) {
      if (flowTokenRef.current !== token) return;
      setPhase("reviewing");
      onError?.(`Couldn't reject the change: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [runId, active, phase, dropActive, onDecided, onError]);

  const revise = useCallback(
    (guidance: string) => {
      if (!runId || !active || phase !== "reviewing") return;
      const trimmed = guidance.trim();
      if (!trimmed) return;
      const token = ++flowTokenRef.current;
      const proposalId = active.proposal.id;
      const scope: ResolveIssueDoc[] = [];
      setPhase("revising");
      setProgressLabel(null);
      setError(null);
      let newProposal: SectionProposal | null = null;
      void reviseProposalStream(
        runId,
        proposalId,
        { guidance: trimmed, library_id: libraryId ?? undefined, document_scope: scope },
        (event) => {
          const type = String(event.type ?? "");
          if (type === "complete") {
            newProposal = {
              id: String(event.proposal_id ?? proposalId),
              module_id: String(event.module_id ?? active.proposal.module_id),
              original_text: String(event.original_text ?? active.proposal.original_text),
              proposed_text: String(event.proposed_text ?? ""),
              issue_ids: active.proposal.issue_ids,
              marker_texts: active.proposal.marker_texts,
              status: "pending",
              computed_section_version: Number(event.section_version ?? active.proposal.computed_section_version),
              revision_count: Number(event.revision_count ?? active.proposal.revision_count + 1),
              supersedes_id: proposalId,
            };
          } else if (type !== "error" && flowTokenRef.current === token) {
            const label = labelForSelectionEditEvent(event, "Rewriting the paragraph…");
            if (label) setProgressLabel(label);
          }
        },
      ).then(
        () => {
          if (flowTokenRef.current !== token) return;
          setProgressLabel(null);
          if (newProposal) {
            const replacement = newProposal;
            setQueue((prev) => prev.map((item, i) => (i === activeIndex ? { ...item, proposal: replacement } : item)));
            setPhase("reviewing");
          } else {
            setPhase("reviewing");
            onError?.("The rewrite ended without a result — please try again.");
          }
        },
        (err) => {
          if (flowTokenRef.current !== token) return;
          setProgressLabel(null);
          setPhase("reviewing");
          onError?.(`Couldn't revise the rewrite: ${err instanceof Error ? err.message : String(err)}`);
        },
      );
    },
    [runId, active, phase, libraryId, activeIndex, onError],
  );

  return {
    phase,
    queue,
    activeIndex,
    active,
    progressLabel,
    error,
    reload,
    next,
    prev,
    select,
    approve,
    reject,
    revise,
  };
}
